import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";

const schema = z.object({
  fullName: z.string().trim().min(2, "Name required").max(100),
  company: z.string().trim().min(2, "Business name required").max(120),
  phone: z.string().trim().min(10, "Valid phone required").max(20),
  email: z.string().trim().email("Invalid email").max(255),
  industry: z.string().min(1, "Select an industry"),
  robotInterest: z.string().min(1, "Select a robot"),
  message: z.string().trim().max(1000).optional(),
});

type FormData = z.infer<typeof schema>;

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  defaultRobot?: "PUDU D7" | "PUDU D9" | "Other Robotics Solution";
}

export const RoboticsConsultationForm = ({ open, onOpenChange, defaultRobot = "PUDU D9" }: Props) => {
  const [submitting, setSubmitting] = useState(false);
  const { toast } = useToast();

  const form = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: {
      fullName: "",
      company: "",
      phone: "",
      email: "",
      industry: "",
      robotInterest: defaultRobot,
      message: "",
    },
  });

  const onSubmit = async (data: FormData) => {
    setSubmitting(true);
    try {
      const { error } = await supabase.functions.invoke("send-qualified-lead", {
        body: {
          name: data.fullName,
          email: data.email,
          phone: data.phone,
          company: data.company,
          industry: data.industry,
          robotInterest: data.robotInterest,
          quantity: "TBD",
          message: data.message || "",
          source: "Humanoid Robotics Consultation",
        },
      });
      if (error) throw error;
      toast({
        title: "Request received",
        description: "A Neil AI robotics specialist will reach out shortly.",
      });
      form.reset();
      onOpenChange(false);
    } catch (e) {
      toast({
        title: "Submission failed",
        description: "Please try again or call (336) 962-4539.",
        variant: "destructive",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-orbitron text-2xl">Request Robotics Consultation</DialogTitle>
          <DialogDescription>
            Neil AI Global Solutions — PUDU Robotics Authorized Dealer. We'll help you scope the right humanoid robotics solution for your business.
          </DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField control={form.control} name="fullName" render={({ field }) => (
              <FormItem><FormLabel>Name</FormLabel><FormControl><Input placeholder="Your full name" {...field} /></FormControl><FormMessage /></FormItem>
            )} />
            <FormField control={form.control} name="company" render={({ field }) => (
              <FormItem><FormLabel>Business Name</FormLabel><FormControl><Input placeholder="Your company" {...field} /></FormControl><FormMessage /></FormItem>
            )} />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormField control={form.control} name="phone" render={({ field }) => (
                <FormItem><FormLabel>Phone</FormLabel><FormControl><Input type="tel" placeholder="(555) 555-5555" {...field} /></FormControl><FormMessage /></FormItem>
              )} />
              <FormField control={form.control} name="email" render={({ field }) => (
                <FormItem><FormLabel>Email</FormLabel><FormControl><Input type="email" placeholder="you@company.com" {...field} /></FormControl><FormMessage /></FormItem>
              )} />
            </div>
            <FormField control={form.control} name="industry" render={({ field }) => (
              <FormItem><FormLabel>Industry</FormLabel>
                <Select onValueChange={field.onChange} value={field.value}>
                  <FormControl><SelectTrigger><SelectValue placeholder="Select industry" /></SelectTrigger></FormControl>
                  <SelectContent>
                    {["Healthcare","Hospitality","Restaurants","Warehousing","Manufacturing","Retail","Senior Living","Commercial Cleaning","Logistics","Other"].map(i => (
                      <SelectItem key={i} value={i}>{i}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )} />
            <FormField control={form.control} name="robotInterest" render={({ field }) => (
              <FormItem><FormLabel>Which robot are you interested in?</FormLabel>
                <Select onValueChange={field.onChange} value={field.value}>
                  <FormControl><SelectTrigger><SelectValue placeholder="Select a robot" /></SelectTrigger></FormControl>
                  <SelectContent>
                    <SelectItem value="PUDU D7">PUDU D7 — Industrial Semi-Humanoid</SelectItem>
                    <SelectItem value="PUDU D9">PUDU D9 — Bipedal Humanoid</SelectItem>
                    <SelectItem value="Other Robotics Solution">Other Robotics Solution</SelectItem>
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )} />
            <FormField control={form.control} name="message" render={({ field }) => (
              <FormItem><FormLabel>Message</FormLabel><FormControl><Textarea placeholder="Tell us about your environment and goals" rows={4} {...field} /></FormControl><FormMessage /></FormItem>
            )} />
            <Button type="submit" size="lg" className="w-full glow-gold" disabled={submitting}>
              {submitting ? "Submitting..." : "Request Robotics Consultation"}
            </Button>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};

export default RoboticsConsultationForm;
